import React, { useEffect, useState, useMemo } from 'react';
import { devLog } from '../../../../utils/devLog';
import {
  Calendar, AlertCircle, AlertTriangle, CheckCircle, Clock,
  FileText, Anchor, Shield, Wrench, Flame,
  Zap, Radio, Heart, Settings, Navigation, Droplets,
  LifeBuoy, Ship, Package, BookOpen, X, User
  // FT-186: Removed Pencil - edit button moved to detail view
} from 'lucide-react';
import { Badge } from '../../../catalyst/badge';
import { summarizeJob } from '../../../../services/equipmentMaintenanceService';
import { getDueSoonThreshold, getJobFrequencyDays } from '../../../../utils/intervalHelpers';
import { getJobDisplayName } from '../../../../utils/maintenanceJobHelpers';
import { useVessel } from '../../../../contexts/VesselContext';
import { useMaintenanceHistory } from '../../../../hooks/domain/maintenance/useMaintenanceHistory';
import { useDefects } from '../../../../hooks/domain/defects/useDefects';
// FT-407: Equipment Register migrated to IndexedDB — read via the IDB-backed hook.
// localStorage.getItem() returned [] post-migration, so the linked-equipment badge
// (itemsTotal/itemsDue/nextDue) never rendered.
import { useEquipmentRegister } from '../../../../hooks/domain/useEquipmentRegister';

// FT-186: Removed onEdit - editing is now in detail view
// FT-353d: onNavigateToDefect surfaces the defect link on maintenance side.
const MaintenanceJobCardEnhanced = ({ job, onComplete, onViewHistory, onViewDetail, onNavigateToDefect }) => {
  const [thumbnail, setThumbnail] = useState(null);
  const [linkedEquipment, setLinkedEquipment] = useState(null);
  const { currentVesselId } = useVessel();
  // FT-407: IDB-backed register (non-archived combined register, reactive).
  const { equipment: equipmentList } = useEquipmentRegister();

  // REV 33: Gemini's Pattern - Hook injection for reactive history
  const { history } = useMaintenanceHistory();

  // FT-353d — resolve the originating defect (if any) so we can render a
  // stale/tombstone chip when the defect was deleted. findById returns
  // undefined for stale ids; the chip still renders (discoverability) but
  // grey and non-clickable.
  const { findById: findDefectById } = useDefects();
  const originatingDefect = job?.originatingDefectId ? findDefectById(job.originatingDefectId) : null;
  const defectIsStale = job?.originatingDefectId && !originatingDefect;

  const handleDefectChipClick = (e) => {
    e.stopPropagation();
    if (defectIsStale) return; // grey tombstone — no navigation
    if (onNavigateToDefect && job?.originatingDefectId) {
      onNavigateToDefect(job.originatingDefectId);
    }
  };


  // Get default image based on equipment type and category
  const getDefaultImage = () => {
    const type = job.equipmentType?.toLowerCase() || '';
    const category = job.category?.toLowerCase() || '';
    const baseUrl = process.env.PUBLIC_URL || '';
    
    // Check specific equipment first
    if (type.includes('fire extinguisher') || type.includes('extinguisher')) {
      return `${baseUrl}/images/maintenance/specific/fire-extinguisher.jpg`;
    }
    // Distinguish between inflatable and foam life jackets
    if (type.includes('inflatable') && (type.includes('life jacket') || type.includes('lifejacket'))) {
      return `${baseUrl}/images/maintenance/specific/life-jacket.jpg`; // This is the inflatable one
    }
    if (type.includes('foam') && (type.includes('life jacket') || type.includes('lifejacket'))) {
      return `${baseUrl}/images/maintenance/categories/lsa.jpg`; // Use generic LSA for foam
    }
    // Default life jacket (if not specified)
    if (type.includes('life jacket') || type.includes('lifejacket')) {
      return `${baseUrl}/images/maintenance/specific/life-jacket.jpg`;
    }
    if (type.includes('liferaft') || type.includes('life raft')) {
      return `${baseUrl}/images/maintenance/specific/liferaft.jpg`;
    }
    if (type.includes('first aid') || type.includes('medical kit')) {
      return `${baseUrl}/images/maintenance/specific/first-aid-kit.jpg`;
    }
    // EPIRB should show generic LSA pic
    if (type.includes('epirb')) {
      return `${baseUrl}/images/maintenance/categories/lsa.jpg`;
    }
    
    // Fall back to category images
    switch(category) {
      case 'lsa':
        return `${baseUrl}/images/maintenance/categories/lsa.jpg`;
      case 'ffe':
        return `${baseUrl}/images/maintenance/categories/ffe.jpg`;
      case 'machinery':
        return `${baseUrl}/images/maintenance/categories/machinery.jpg`;
      case 'electrical':
        return `${baseUrl}/images/maintenance/categories/electrical.jpg`;
      case 'navigation':
        return `${baseUrl}/images/maintenance/categories/navigation.jpg`;
      case 'radio':
        return `${baseUrl}/images/maintenance/categories/radio.jpg`;
      case 'medical':
        return `${baseUrl}/images/maintenance/categories/medical.jpg`;
      case 'surveys':
        return `${baseUrl}/images/maintenance/categories/surveys.jpg`;
      case 'special_ops':
        return `${baseUrl}/images/maintenance/categories/special-ops.jpg`;
      default:
        return `${baseUrl}/images/maintenance/categories/default.jpg`;
    }
  };
  
  // Load thumbnail from localStorage or use default
  useEffect(() => {
    const thumbKey = `equipment_image_${job.id}_thumbnail`;
    const savedThumb = localStorage.getItem(thumbKey);
    if (savedThumb) {
      setThumbnail(savedThumb);
    } else {
      // Use default image if no custom thumbnail
      const defaultImg = getDefaultImage();
      devLog('Loading default image for', job.equipmentType, ':', defaultImg);
      setThumbnail(defaultImg);
    }
  }, [job.id, job.equipmentType, job.category]);

  // Load linked equipment summary (flat structure)
  useEffect(() => {
    const loadEquipmentSummary = () => {
      if (!job.linkedEquipmentIds || job.linkedEquipmentIds.length === 0 || !currentVesselId) {
        setLinkedEquipment(null);
        return;
      }

      // FT-407: equipment now comes from the IDB-backed hook (equipmentList) instead
      // of localStorage.getItem(), which returned [] after the register moved to IDB.
      // Create equipment map for summary function
      const equipmentMap = new Map(equipmentList.map(eq => [eq.id, eq]));

      // Get roll-up summary
      const summary = summarizeJob(job, equipmentMap);
      // summary = { itemsTotal, itemsDue, nextDue, dueItems }

      if (summary.itemsTotal > 0) {
        setLinkedEquipment(summary);
        devLog('[MaintenanceJobCard] Badge updated:', summary);
      } else {
        setLinkedEquipment(null);
      }
    };

    // Initial load
    loadEquipmentSummary();

    // Listen for equipment updates (from MaintenanceModule after completion)
    const handleEquipmentUpdate = (event) => {
      if (event.detail?.vesselId === currentVesselId) {
        devLog('[MaintenanceJobCard] Received equipment update event, refreshing badge');
        loadEquipmentSummary();
      }
    };

    window.addEventListener('sr:equipment:updated', handleEquipmentUpdate);

    // Cleanup
    return () => {
      window.removeEventListener('sr:equipment:updated', handleEquipmentUpdate);
    };
    // FT-407: `equipmentList` added to deps — the IDB register loads asynchronously,
    // so the badge must recompute when the hook's data arrives (was [job, currentVesselId]).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, currentVesselId, equipmentList]);
  // Get icon based on equipment type
  const getEquipmentIcon = () => {
    const type = job.equipmentType?.toLowerCase() || '';
    const category = job.category?.toLowerCase() || '';
    
    if (type.includes('fire') || type.includes('extinguisher')) return Flame;
    if (type.includes('life') && (type.includes('jacket') || type.includes('vest'))) return LifeBuoy;
    if (type.includes('liferaft') || type.includes('life raft')) return Ship;
    if (type.includes('lifebuoy')) return LifeBuoy;
    if (type.includes('epirb')) return Radio;
    if (type.includes('first aid') || type.includes('medical')) return Heart;
    if (type.includes('navigation') || type.includes('nav')) return Navigation;
    if (type.includes('engine') || type.includes('machinery')) return Settings;
    if (type.includes('electrical')) return Zap;
    if (type.includes('radio') || type.includes('communication')) return Radio;
    if (type.includes('anchor')) return Anchor;
    if (type.includes('pump') || type.includes('bilge')) return Droplets;
    if (type.includes('survey')) return FileText;
    
    // Category fallbacks
    if (category === 'lsa') return LifeBuoy;
    if (category === 'ffe') return Flame;
    if (category === 'machinery') return Settings;
    if (category === 'electrical') return Zap;
    if (category === 'navigation') return Navigation;
    if (category === 'radio') return Radio;
    if (category === 'medical') return Heart;
    
    return Wrench; // Default
  };

  // Get gradient based on category
  const getCategoryGradient = () => {
    switch (job.category) {
      case 'surveys':
        return 'from-cyan-500 to-purple-600';
      case 'lsa':
        return 'from-blue-500 to-cyan-600';
      case 'ffe':
        return 'from-red-500 to-orange-600';
      case 'machinery':
        return 'from-yellow-500 to-amber-600';
      case 'electrical':
        return 'from-green-500 to-emerald-600';
      case 'navigation':
        return 'from-purple-500 to-pink-600';
      case 'radio':
        return 'from-pink-500 to-rose-600';
      case 'medical':
        return 'from-cyan-500 to-teal-600';
      case 'special_ops':
        return 'from-orange-500 to-red-600';
      default:
        return 'from-gray-500 to-slate-600';
    }
  };

  // Format date - moved before getStatusDisplay to avoid hoisting issue
  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  // REV 33: Gemini's Pattern - Derive last completed date from reactive history
  // FT-400 follow-up: history records carry signedOffStatus, the date-only
  // fallbacks do NOT. Check history FIRST so the sign-off badge fires even when
  // job.lastCompleted / linkedEquipment.lastCompleted are set (which is the
  // common case in production). Fall back to date-only paths ONLY when there is
  // no matching history record. History records carry completedAt (epoch ms),
  // so getLastCompletedDate()/calculateNextDue() remain compatible.
  const lastCompletion = useMemo(() => {
    // Prefer reactive history from hook — it carries signedOffStatus.
    if (history && job?.id) {
      const historyRecord = history
        .filter(h => h.jobId === job.id)
        .sort((a, b) => (b.completedAt || 0) - (a.completedAt || 0))[0];
      if (historyRecord) return historyRecord;
    }

    // Fall back to date-only sources only when no history record exists.
    if (linkedEquipment?.lastCompleted) return { completedAt: linkedEquipment.lastCompleted };
    if (job.lastCompleted) return { completedAt: job.lastCompleted };

    return null;
  }, [history, job.id, linkedEquipment?.lastCompleted, job.lastCompleted]);

  // Extract date for backward compatibility
  const getLastCompletedDate = () => {
    return lastCompletion?.completedAt || lastCompletion?.date || null;
  };

  // FT-140: Calculate next due date using getJobFrequencyDays() for consistency with Dashboard
  // This ensures both Dashboard and Maintenance page show the same dates
  const calculateNextDue = () => {
    // Use the new helper function to get last completed date
    const lastDate = getLastCompletedDate();

    if (lastDate && (job.interval || job.frequencyMonths)) {
      // Get frequency in days using the same helper as Dashboard
      const frequencyDays = getJobFrequencyDays(job);

      // Calculate next due from last completed + frequency days
      const last = new Date(lastDate);
      const next = new Date(last);
      next.setDate(last.getDate() + frequencyDays);

      return next.toISOString();
    }

    // Fall back to any existing next due date
    return linkedEquipment?.nextDue || job.nextDue || null;
  };

  // Calculate job status based on next due date and job frequency
  const calculateStatus = (nextDueDate) => {
    if (!nextDueDate) return 'unknown';

    const today = new Date();
    const dueDate = new Date(nextDueDate);
    const daysUntilDue = Math.floor((dueDate - today) / (1000 * 60 * 60 * 24));

    const threshold = getDueSoonThreshold(job);
    if (daysUntilDue < 0) return 'overdue';
    if (daysUntilDue <= threshold) return 'due_soon';
    return 'compliant';
  };

  // Get status display - now shows next due date instead of days
  const getStatusDisplay = () => {
    // Calculate next due date to display
    const nextDueDate = calculateNextDue();
    const nextDateLabel = nextDueDate ? `Next: ${formatDate(nextDueDate)}` : 'No due date';
    
    // Calculate status from due date if not provided
    const status = job.status || calculateStatus(nextDueDate);

    switch (status) {
      case 'overdue':
        return {
          icon: AlertCircle,
          color: 'text-red-600',
          bgColor: 'bg-red-100',
          borderColor: 'border-red-200',
          label: nextDateLabel,
          priority: 'high'
        };
      case 'due_soon':
        return {
          icon: AlertTriangle,
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100',
          borderColor: 'border-yellow-200',
          label: nextDateLabel,
          priority: 'medium'
        };
      case 'compliant':
        return {
          icon: CheckCircle,
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          borderColor: 'border-green-200',
          label: nextDateLabel,
          priority: 'low'
        };
      default:
        return {
          icon: Clock,
          color: 'text-gray-500',
          bgColor: 'bg-gray-100',
          borderColor: 'border-gray-200',
          label: nextDateLabel || 'No due date',
          priority: 'none'
        };
    }
  };

  const statusDisplay = getStatusDisplay();
  const StatusIcon = statusDisplay.icon;
  const EquipmentIcon = getEquipmentIcon();

  // Get interval display
  const getIntervalDisplay = () => {
    // Guard against missing interval/unit
    if (!job.interval || !job.unit) {
      // Try to fall back to frequencyMonths if available
      if (job.frequencyMonths) {
        if (job.frequencyMonths === 1) return 'Monthly';
        if (job.frequencyMonths === 3) return 'Quarterly';
        if (job.frequencyMonths === 6) return '6 Monthly';
        if (job.frequencyMonths === 12) return 'Annual';
        if (job.frequencyMonths === 60) return '5 Yearly';
        if (job.frequencyMonths === 120) return '10 Yearly';
        return `Every ${job.frequencyMonths} months`;
      }
      // Return empty string if no interval information available
      return '';
    }
    
    // Special interval strings
    if (job.interval === 'At survey') return 'At survey';
    if (job.interval === 'Before departure') return 'Before departure';
    
    // Standard intervals
    if (job.interval === 120 && job.unit === 'months') return '10 Yearly';
    if (job.interval === 60 && job.unit === 'months') return '5 Yearly';
    if (job.interval === 12 && job.unit === 'months') return 'Annual';
    if (job.interval === 6 && job.unit === 'months') return '6 Monthly';
    if (job.interval === 3 && job.unit === 'months') return 'Quarterly';
    if (job.interval === 1 && job.unit === 'months') return 'Monthly';
    if (job.interval === 1 && job.unit === 'month') return 'Monthly';
    
    // Format generic intervals nicely
    if (job.unit === 'day' || job.unit === 'days') {
      return job.interval === 1 ? 'Daily' : `Every ${job.interval} days`;
    }
    if (job.unit === 'week' || job.unit === 'weeks') {
      return job.interval === 1 ? 'Weekly' : `Every ${job.interval} weeks`;
    }
    if (job.unit === 'month' || job.unit === 'months') {
      return job.interval === 1 ? 'Monthly' : `Every ${job.interval} months`;
    }
    if (job.unit === 'year' || job.unit === 'years') {
      return job.interval === 1 ? 'Annual' : `Every ${job.interval} years`;
    }
    
    return `Every ${job.interval} ${job.unit}`;
  };

  // Get category display name
  const getCategoryName = () => {
    const names = {
      surveys: 'Surveys',
      lsa: 'Life Saving',
      ffe: 'Fire Fighting',
      machinery: 'Machinery',
      electrical: 'Electrical',
      navigation: 'Navigation',
      radio: 'Radio',
      medical: 'Medical',
      special_ops: 'Special Ops'
    };
    return names[job.category] || job.category;
  };


  return (
    <div 
      className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer overflow-hidden border border-gray-200"
      onClick={() => onViewDetail && onViewDetail(job)}
    >
      <div className="p-4 sm:p-4">
        <div className="flex items-start gap-3 sm:gap-3">
          {/* Thumbnail - always show image now that we have defaults */}
          <div className="flex-shrink-0">
            {thumbnail ? (
              <img
                src={thumbnail}
                alt={getJobDisplayName(job)}
                className="w-14 h-14 md:w-16 md:h-16 rounded-lg object-cover shadow-sm"
                onError={(e) => {

                  // Fallback to icon if image fails to load
                  setThumbnail(null);
                }}
              />
            ) : (
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-lg bg-gray-100 flex items-center justify-center">
                <EquipmentIcon className="h-7 w-7 md:h-8 md:w-8 text-gray-400" />
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Mobile: stacked layout (title full width, badge below). Desktop: side by side. */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-1">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <h3 className="text-sm sm:text-sm font-semibold sm:font-medium text-gray-900 line-clamp-2 sm:truncate">
                    {getJobDisplayName(job)}
                  </h3>
                  {/* Show open-flame badge if this job covers that requirement */}
                  {(job?.meta?.coversRequirement === 'ffe_protection_open_flame' ||
                    job?.sourceRequirements?.includes('ffe_protection_open_flame')) && (
                    <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-medium bg-orange-100 text-orange-800 rounded-full whitespace-nowrap">
                      🔥 Open Flame
                    </span>
                  )}
                </div>
                <p className="hidden sm:block text-xs text-gray-500 mt-0.5 line-clamp-1 sm:truncate">
                  {job.requirement || job.checkType || (job.isStatutory ? 'Required maintenance' : (job.isRecommended ? 'Recommended maintenance' : 'Scheduled maintenance'))}
                </p>
              </div>

              {/* Status Badge - now shows next date */}
              <div className="flex items-center gap-1 mt-1 sm:mt-0">
                {/* FT-209: Job Type Badge - only show for corrective jobs to reduce visual clutter */}
                {/* FT-353d: added data-testid so the badge is asserted from tests. */}
                {job.jobType === 'corrective' && (
                  <span data-testid="maintenance-job-card-corrective-badge">
                    <Badge color="orange" className="text-[10px] sm:text-xs">Corrective</Badge>
                  </span>
                )}
                {/* FT-209: One-time indicator for corrective jobs */}
                {job.isOneTime && (
                  <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[9px] font-medium bg-gray-100 text-gray-600 rounded-full whitespace-nowrap">
                    One-Time
                  </span>
                )}
                {/* FT-183: Assigned to badge - matches existing Badge component pattern */}
                {job.assignedTo && (
                  <Badge color="blue" className="text-[10px] sm:text-xs">
                    <User className="h-3 w-3 mr-0.5" />
                    {job.assignedTo}
                  </Badge>
                )}
                {/* FT-400: Sign-off badges removed from planned-maintenance list (FT-451).
                    Sign-off is a history concept — a recurring job stays due/overdue
                    after it's next due even if the last completion was signed off.
                    Sign-off status is still shown in MaintenanceHistoryView. */}
                {/* FT-186: Edit button removed from list cards - editing is now in detail view */}
                <span className={`inline-flex items-center px-2 sm:px-2 py-1 sm:py-0.5 rounded-full text-xs sm:text-xs font-semibold sm:font-medium ${statusDisplay.bgColor} ${statusDisplay.color}`}>
                  <StatusIcon className="h-3 sm:h-3 w-3 sm:w-3 mr-1 sm:mr-1" />
                  <span>{statusDisplay.label}</span>
                </span>
              </div>
            </div>
            
            {/* Meta Information - simplified on mobile: just frequency. Full detail on desktop. */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mt-1.5 sm:mt-2">
              {getIntervalDisplay() && (
                <span className="inline-flex items-center text-xs sm:text-xs text-gray-500">
                  <Clock className="h-3 sm:h-3 w-3 sm:w-3 mr-1 sm:mr-1" />
                  {getIntervalDisplay()}
                </span>
              )}

              <span className="hidden sm:inline-flex items-center text-xs text-gray-500">
                <Calendar className="h-3 w-3 mr-1" />
                Last: {formatDate(getLastCompletedDate())}
              </span>

              {job.regulation && (
                <span className="hidden sm:inline-flex items-center text-xs text-gray-500">
                  <Shield className="h-3 w-3 mr-1" />
                  {job.regulation}
                </span>
              )}

              {/* FT-353d: Defect link mini-chip — cyan pill mirroring
                  FT-353b's "View job" chip on the defect side. Grey
                  tombstone when the originating defect is gone. */}
              {job.originatingDefectId && (
                <button
                  type="button"
                  onClick={handleDefectChipClick}
                  disabled={defectIsStale}
                  title={defectIsStale
                    ? 'Defect not found'
                    : `View originating defect ${job.originatingDefectId}`}
                  data-testid="maintenance-job-card-defect-chip"
                  className={
                    defectIsStale
                      ? 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-50 text-slate-400 border border-slate-200 cursor-not-allowed'
                      : 'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-cyan-50 text-cyan-700 border border-cyan-200 hover:bg-cyan-100 transition-colors'
                  }
                >
                  <Wrench className="h-3 w-3" aria-hidden="true" />
                  → DEF-{String(job.originatingDefectId).slice(-5)}
                </button>
              )}
            </div>
            
            {/* Category label - hidden on mobile, visible on desktop */}
            <div className="hidden sm:flex items-center justify-end mt-3">
              <span className="text-xs text-gray-400 uppercase tracking-wider">
                {getCategoryName()}
              </span>
            </div>
          </div>
        </div>

        {/* Equipment count removed - cleaner cards, info available on detail page */}

        {/* Additional Warnings */}
        {(job.warning || job.certificate) && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            {job.warning && (
              <p className="text-xs text-orange-600 flex items-start">
                <AlertTriangle className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0" />
                {job.warning}
              </p>
            )}
            {job.certificate && (
              <p className="text-xs text-blue-600 flex items-start mt-1">
                <Shield className="h-3 w-3 mr-1 mt-0.5 flex-shrink-0" />
                Certificate required: {job.certificate}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MaintenanceJobCardEnhanced;